import { useState, useRef, useContext, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import StockDamageTable from "./StockDamageTable";
import ProductContext from "context/ProductContext";
import { ROUTES, KEYBOARD_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import { useBarcodeScanner } from "helpers/BarcodeHelper";
import toast from "react-hot-toast";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import StockDamageHelper from "helpers/StockDamageHelper";
import BackIcon from "images/svg/back-icon.svg";

function AddStockDamage() {
  const navigate = useNavigate();
  const { availableItems, items } = useContext(ProductContext);

  const [damageItems, setDamageItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const productInputRef = useRef(null);

  const allProducts = useMemo(
    () => (items && items.length > 0 ? items : availableItems || []),
    [items, availableItems],
  );

  const handleDamageScanCode = useCallback(
    (scannedCode) => {
      StockDamageHelper.processStockDamageScanCode(
        scannedCode,
        allProducts,
        quantity,
        setDamageItems,
        setSelectedProduct,
        setQuantity,
        productInputRef,
      );
    },
    [allProducts, quantity, setDamageItems, setSelectedProduct, setQuantity],
  );

  const scannerOptions = useMemo(() => ({ isDisabled: isSaving }), [isSaving]);

  useBarcodeScanner(handleDamageScanCode, scannerOptions);

  const handleAddItemToDamage = () => {
    if (!selectedProduct) return;

    const qty = Number(quantity) || 1;
    const newItem = {
      cartId: Date.now(),
      productId: selectedProduct.id,
      plu: selectedProduct.plu,
      name: selectedProduct.name,
      qty: qty,
      cost: selectedProduct.cost || 0,
      price: selectedProduct.price,
      payable: selectedProduct.cost * qty,
      category: selectedProduct.category,
    };

    setDamageItems((prev) => [...prev, newItem]);
    setSelectedProduct(null);
    setQuantity("");

    setTimeout(() => {
      productInputRef.current?.focus();
    }, 100);
  };

  const handleUpdateRowProduct = (row, newProduct) => {
    if (!newProduct) return;
    setDamageItems((prev) =>
      prev.map((item) =>
        item.cartId === row.cartId
          ? {
              ...item,
              productId: newProduct.id,
              plu: newProduct.plu,
              name: newProduct.name,
              cost: newProduct.cost || 0,
              price: newProduct.price,
              payable: newProduct.cost * item.qty,
              category: newProduct.category,
            }
          : item,
      ),
    );
  };

  const handleUpdateRowQuantity = (row, newQty) => {
    const qty = Number(newQty);
    if (qty <= 0) return;
    setDamageItems((prev) =>
      prev.map((item) =>
        item.cartId === row.cartId
          ? {
              ...item,
              qty: qty,
              payable: item.cost * qty,
            }
          : item,
      ),
    );
  };

  const handleDeleteRow = (row) => {
    setDamageItems((prev) => prev.filter((item) => item.cartId !== row.cartId));
  };

  const handleSaveDamage = useCallback(async () => {
    if (damageItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setIsSaving(true);
    try {
      await StockDamageHelper.saveStockDamageToCloudAndLocal(damageItems);
      toast.success("Stock damages recorded successfully");
      navigate(ROUTES.STOCK_DAMAGES);
    } catch (error) {
      console.error("Failed to save damage", error);
      toast.error(error.message || "Failed to save stock damage");
    } finally {
      setIsSaving(false);
    }
  }, [damageItems, navigate]);

  const handleSaveDraft = useCallback(async () => {
    if (damageItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    try {
      setIsSaving(true);
      await StockDamageHelper.saveDraftStockDamage(damageItems);
      toast.success("Draft saved successfully");
      navigate(ROUTES.STOCK_DAMAGES);
    } catch (error) {
      console.error("Failed to save draft", error);
      toast.error("Failed to save draft");
    } finally {
      setIsSaving(false);
    }
  }, [damageItems, navigate]);

  useEffect(() => {
    setTimeout(() => {
      productInputRef.current?.focus();
      productInputRef.current?.select?.();
    }, 200);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSaving) return;

      if (e.key === KEYBOARD_KEYS.F1) {
        e.preventDefault();
        e.stopPropagation();
        productInputRef.current?.focus();
        productInputRef.current?.select?.();
        return;
      }

      if (e.key === KEYBOARD_KEYS.F4) {
        e.preventDefault();
        e.stopPropagation();
        handleSaveDamage();
        return;
      }

      if (e.key === KEYBOARD_KEYS.F5) {
        e.preventDefault();
        e.stopPropagation();
        handleSaveDraft();
        return;
      }
    };
    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [isSaving, handleSaveDamage, handleSaveDraft]);

  return (
    <div style={{ padding: "10px 10px 0 10px", height: "100%", boxSizing: "border-box" }}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.leftHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(ROUTES.STOCK_DAMAGES)}
              aria-label="Back to Stock Damages"
            >
              <img src={BackIcon} alt="Back to Stock Damages" />
            </button>

            <div className={styles.header}>
              <h2>Add New Stock Damage</h2>
              <h5>Record and Track Stock Damages</h5>
            </div>
          </div>
          <div className={`${styles.contentWrapper} ${styles.buttonsection}`}>
            <ButtonGray onClick={() => navigate(ROUTES.STOCK_DAMAGES)} disabled={isSaving}>
              Cancel
            </ButtonGray>
            <ButtonGray onClick={handleSaveDraft} disabled={isSaving}>
              Save As Draft (F5)
            </ButtonGray>
            <ButtonRed onClick={handleSaveDamage} disabled={isSaving} style={{ minWidth: "120px" }}>
              {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save Damage (F4)"}
            </ButtonRed>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <StockDamageTable
            saleItems={damageItems}
            items={availableItems}
            handleUpdateRowProduct={handleUpdateRowProduct}
            handleUpdateRowQuantity={handleUpdateRowQuantity}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            quantity={quantity}
            setQuantity={setQuantity}
            handleAddProduct={handleAddItemToDamage}
            handleDeleteRow={handleDeleteRow}
            productInputRef={productInputRef}
          />
        </div>
      </div>
    </div>
  );
}

export default AddStockDamage;
